import { Link, type LinkProps } from 'react-router-dom';

/*
 * Shared button primitives. Two flavours that share the exact same look:
 *   <Button>      — a real <button> (onClick actions, submit, etc.)
 *   <LinkButton>  — a react-router <Link> styled as a button (navigation)
 *
 * Styling lives in styles/ui.css (.ui-btn + variant/size modifiers) so we get
 * genuine :hover / :active feedback, which inline styles can't do. Pass extra
 * inline `style` to tweak per-use (e.g. width:'100%') without losing that.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg';

function classes(variant: Variant, size: Size, extra?: string) {
  return `ui-btn ui-btn--${variant} ui-btn--${size}${extra ? ' ' + extra : ''}`;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

export function Button({ variant = 'primary', size = 'md', className, ...rest }: ButtonProps) {
  return <button className={classes(variant, size, className)} {...rest} />;
}

interface LinkButtonProps extends Omit<LinkProps, 'className'> {
  variant?: Variant;
  size?: Size;
  className?: string;
}

export function LinkButton({ variant = 'primary', size = 'md', className, ...rest }: LinkButtonProps) {
  return <Link className={classes(variant, size, className)} {...rest} />;
}
